/**
 * AI Suggestions Sidebar Component
 * 
 * Displays AI-generated suggestions for YAML improvements with
 * diff preview and accept/reject functionality.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  makeStyles,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Collapse,
  Box,
  Tooltip,
  Paper,
  CircularProgress,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SpellcheckIcon from '@material-ui/icons/Spellcheck';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';
import type { AiSuggestion, AiSuggestionType } from '../../api';

export const AI_SIDEBAR_DEFAULT_WIDTH = 380;
export const AI_SIDEBAR_MIN_WIDTH = 300;
export const AI_SIDEBAR_MAX_WIDTH = 600;

const useStyles = makeStyles(theme => ({
  sidebarContainer: {
    position: 'relative',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 10,
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  resizeHandleActive: {
    backgroundColor: theme.palette.primary.main,
  },
  sidebar: {
    width: '100%',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  content: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  loadingIcon: {
    marginBottom: theme.spacing(2),
  },
  noSuggestions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    color: theme.palette.success.main,
    textAlign: 'center',
  },
  noSuggestionsIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme.palette.success.light,
  },
  summary: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.02)',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  suggestionsList: {
    maxHeight: 200,
    overflowY: 'auto',
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  suggestionsListItem: {
    cursor: 'pointer',
    padding: theme.spacing(1, 1.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedSuggestion: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  suggestionIcon: {
    minWidth: 32,
  },
  typeChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.5),
  },
  addChip: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  modifyChip: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  removeChip: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  typoChip: {
    backgroundColor: '#9c27b0',
    color: '#fff',
  },
  detailsSection: {
    marginBottom: theme.spacing(2),
  },
  diffContainer: {
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  diffHeader: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.04)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  diffContent: {
    padding: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  removedLine: {
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(244, 67, 54, 0.2)' 
      : 'rgba(244, 67, 54, 0.1)',
    color: theme.palette.error.main,
    padding: theme.spacing(0.25, 0.5),
    marginBottom: 2,
    borderRadius: 2,
    display: 'block',
    '&::before': {
      content: '"- "',
    },
  },
  addedLine: {
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(76, 175, 80, 0.2)' 
      : 'rgba(76, 175, 80, 0.1)',
    color: theme.palette.success.main,
    padding: theme.spacing(0.25, 0.5),
    marginBottom: 2,
    borderRadius: 2,
    display: 'block',
    '&::before': {
      content: '"+ "',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  acceptButton: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  rejectButton: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
  pathChip: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    height: 24,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  processingTime: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
  modelSelector: {
    margin: theme.spacing(1, 2),
    marginTop: 0,
  },
  modelFormControl: {
    width: '100%',
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
    },
    '& .MuiSelect-root': {
      fontSize: '0.8rem',
      padding: theme.spacing(0.75, 1),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  },
  modelMenuItem: {
    fontSize: '0.8rem',
  },
}));

export interface AiSuggestionsSidebarProps {
  open: boolean;
  onClose: () => void;
  suggestions: AiSuggestion[];
  selectedSuggestion: AiSuggestion | null;
  onSelectSuggestion: (suggestion: AiSuggestion | null) => void;
  onAcceptSuggestion: (suggestion: AiSuggestion) => void;
  onRejectSuggestion: (suggestion: AiSuggestion) => void;
  onGoToLine?: (line: number, column?: number) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  summary?: string;
  processingTime?: number;
  error?: string;
  // Model selection
  availableModels?: string[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  isLoadingModels?: boolean;
}

const getSuggestionTypeIcon = (type: AiSuggestionType) => {
  switch (type) {
    case 'add':
      return <AddCircleOutlineIcon fontSize="small" style={{ color: '#4caf50' }} />;
    case 'modify':
      return <EditIcon fontSize="small" style={{ color: '#ff9800' }} />;
    case 'remove':
      return <DeleteOutlineIcon fontSize="small" style={{ color: '#f44336' }} />;
    case 'typo':
      return <SpellcheckIcon fontSize="small" style={{ color: '#9c27b0' }} />;
    default:
      return <EditIcon fontSize="small" />;
  }
};

const getTypeChipClass = (type: AiSuggestionType, classes: ReturnType<typeof useStyles>) => {
  switch (type) {
    case 'add':
      return classes.addChip;
    case 'modify':
      return classes.modifyChip;
    case 'remove':
      return classes.removeChip;
    case 'typo':
      return classes.typoChip;
    default:
      return '';
  }
};

export const AiSuggestionsSidebar = ({
  open,
  onClose,
  suggestions,
  selectedSuggestion,
  onSelectSuggestion,
  onAcceptSuggestion,
  onRejectSuggestion,
  onGoToLine,
  onRefresh,
  isLoading = false,
  summary,
  processingTime,
  error,
  availableModels = [],
  selectedModel = '',
  onModelChange,
  isLoadingModels = false,
}: AiSuggestionsSidebarProps) => {
  const classes = useStyles();
  const [sidebarWidth, setSidebarWidth] = useState(AI_SIDEBAR_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [diffExpanded, setDiffExpanded] = useState(true);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(AI_SIDEBAR_DEFAULT_WIDTH);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  }, [sidebarWidth]);

  // Handle resize move and end
  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.min(
        AI_SIDEBAR_MAX_WIDTH,
        Math.max(AI_SIDEBAR_MIN_WIDTH, resizeStartWidth.current + deltaX)
      );
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  const handleGoToSuggestion = useCallback(() => {
    if (selectedSuggestion?.line && onGoToLine) {
      onGoToLine(selectedSuggestion.line);
    }
  }, [selectedSuggestion, onGoToLine]);

  if (!open) {
    return null;
  }

  // Group suggestions by type and severity
  const errorCount = suggestions.filter(s => s.severity === 'error').length;
  const warningCount = suggestions.filter(s => s.severity === 'warning').length;
  const addCount = suggestions.filter(s => s.type === 'add').length;
  const modifyCount = suggestions.filter(s => s.type === 'modify').length;
  const typoCount = suggestions.filter(s => s.type === 'typo').length;
  const removeCount = suggestions.filter(s => s.type === 'remove').length;

  return (
    <div className={classes.sidebarContainer} style={{ width: sidebarWidth }}>
      {/* Resize Handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
      />
      <Paper className={classes.sidebar} elevation={0} square>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <EmojiObjectsIcon color="primary" />
            <Typography variant="subtitle2">AI Suggestions</Typography>
            <Chip 
              size="small" 
              label="Experimental"
              style={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(156, 39, 176, 0.2)', color: '#ce93d8' }}
            />
            {suggestions.length > 0 && (
              <Chip 
                size="small" 
                label={suggestions.length}
                color="primary"
                style={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </div>
          <div className={classes.headerActions}>
            {onRefresh && (
              <Tooltip title="Refresh analysis">
                <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Model Selector */}
        {availableModels.length > 0 && (
          <div className={classes.modelSelector}>
            <FormControl variant="outlined" size="small" className={classes.modelFormControl}>
              <InputLabel id="ai-model-select-label">Model</InputLabel>
              <Select
                labelId="ai-model-select-label"
                id="ai-model-select"
                value={selectedModel}
                onChange={(e) => onModelChange?.(e.target.value as string)}
                label="Model"
                disabled={isLoading || isLoadingModels}
              >
                {availableModels.map((model) => (
                  <MenuItem key={model} value={model} className={classes.modelMenuItem}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <LinearProgress color="primary" />
        )}

        {/* Content */}
        <div className={classes.content}>
          {/* Loading */}
          {isLoading && (
            <div className={classes.loadingContainer}>
              <CircularProgress size={40} className={classes.loadingIcon} />
              <Typography variant="subtitle2">Analyzing with AI...</Typography>
              <Typography variant="caption" color="textSecondary">
                Checking for errors, typos, and missing fields
              </Typography>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <Box textAlign="center" p={3}>
              <Typography color="error" variant="subtitle2">
                Analysis Failed
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {error}
              </Typography>
              {onRefresh && (
                <Box mt={2}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                  >
                    Try Again
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* No Suggestions */}
          {!isLoading && !error && suggestions.length === 0 && (
            <div className={classes.noSuggestions}>
              <CheckIcon className={classes.noSuggestionsIcon} />
              <Typography variant="subtitle2">Looking Good!</Typography>
              <Typography variant="caption" color="textSecondary">
                {summary || 'No suggestions at this time.'}
              </Typography>
              {onRefresh && (
                <Box mt={2}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                  >
                    Analyze Again
                  </Button>
                </Box>
              )}
            </div>
          )}

          {/* Suggestions */}
          {!isLoading && !error && suggestions.length > 0 && (
            <>
              {/* AI Disclaimer */}
              <Box 
                mb={1.5} 
                p={1} 
                bgcolor="rgba(255, 152, 0, 0.08)" 
                borderRadius={1}
                border="1px solid rgba(255, 152, 0, 0.3)"
              >
                <Typography variant="caption" style={{ color: '#b26a00', fontStyle: 'italic' }}>
                  ⚠️ AI suggestions may not always be accurate. Please review and validate before applying.
                </Typography>
              </Box>

              {/* Summary */}
              {summary && (
                <div className={classes.summary}>
                  <Typography variant="caption" color="textSecondary">
                    {summary}
                  </Typography>
                  {processingTime && (
                    <div className={classes.processingTime}>
                      Analyzed in {processingTime}ms
                    </div>
                  )}
                </div>
              )}

              {/* Quick Stats - Priority Order */}
              <Box display="flex" flexWrap="wrap" style={{ gap: 4 }} mb={2}>
                {errorCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<ErrorOutlineIcon style={{ fontSize: 14, color: '#f44336' }} />}
                    label={`${errorCount} error${errorCount > 1 ? 's' : ''}`}
                    style={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#f44336', borderColor: '#f44336' }}
                    variant="outlined"
                  />
                )}
                {typoCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<SpellcheckIcon style={{ fontSize: 14 }} />}
                    label={`${typoCount} typo${typoCount > 1 ? 's' : ''}`}
                    className={`${classes.typeChip} ${classes.typoChip}`}
                  />
                )}
                {warningCount > 0 && warningCount !== typoCount && (
                  <Chip 
                    size="small" 
                    icon={<WarningIcon style={{ fontSize: 14, color: '#ff9800' }} />}
                    label={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}
                    style={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', borderColor: '#ff9800' }}
                    variant="outlined"
                  />
                )}
                {addCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<AddCircleOutlineIcon style={{ fontSize: 14 }} />}
                    label={`${addCount} addition${addCount > 1 ? 's' : ''}`}
                    className={`${classes.typeChip} ${classes.addChip}`}
                  />
                )}
                {modifyCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<EditIcon style={{ fontSize: 14 }} />}
                    label={`${modifyCount} change${modifyCount > 1 ? 's' : ''}`}
                    className={`${classes.typeChip} ${classes.modifyChip}`}
                  />
                )}
                {removeCount > 0 && (
                  <Chip 
                    size="small" 
                    icon={<DeleteOutlineIcon style={{ fontSize: 14 }} />}
                    label={`${removeCount} removal${removeCount > 1 ? 's' : ''}`}
                    className={`${classes.typeChip} ${classes.removeChip}`}
                  />
                )}
              </Box>

              {/* Suggestions List */}
              <div className={classes.detailsSection}>
                <Typography className={classes.sectionTitle}>
                  Suggestions
                </Typography>
                <List className={classes.suggestionsList} dense disablePadding>
                  {suggestions.map((suggestion) => (
                    <ListItem
                      key={suggestion.id}
                      className={`${classes.suggestionsListItem} ${
                        selectedSuggestion?.id === suggestion.id ? classes.selectedSuggestion : ''
                      }`}
                      onClick={() => {
                        onSelectSuggestion(
                          selectedSuggestion?.id === suggestion.id ? null : suggestion
                        );
                      }}
                      button
                    >
                      <ListItemIcon className={classes.suggestionIcon}>
                        {getSuggestionTypeIcon(suggestion.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            {suggestion.severity === 'error' && (
                              <ErrorOutlineIcon style={{ fontSize: 14, color: '#f44336' }} />
                            )}
                            {suggestion.severity === 'warning' && (
                              <WarningIcon style={{ fontSize: 14, color: '#ff9800' }} />
                            )}
                            <Typography variant="caption" style={{ fontWeight: 500, flex: 1 }}>
                              {suggestion.title}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={suggestion.type}
                              className={`${classes.typeChip} ${getTypeChipClass(suggestion.type, classes)}`}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            style={{ fontSize: '0.7rem', display: 'block', marginTop: 2 }}
                          >
                            Line {suggestion.line} • {suggestion.path}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </div>

              <Divider />

              {/* Selected Suggestion Details */}
              {selectedSuggestion ? (
                <Box mt={2}>
                  {/* Title & Type */}
                  <div className={classes.detailsSection}>
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                      {getSuggestionTypeIcon(selectedSuggestion.type)}
                      <Box flex={1}>
                        <Typography variant="subtitle2">
                          {selectedSuggestion.title}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={selectedSuggestion.type}
                          className={`${classes.typeChip} ${getTypeChipClass(selectedSuggestion.type, classes)}`}
                          style={{ marginTop: 4 }}
                        />
                      </Box>
                    </Box>
                  </div>

                  {/* Description */}
                  <div className={classes.detailsSection}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={detailsExpanded}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setDetailsExpanded(!detailsExpanded)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDetailsExpanded(!detailsExpanded); }}
                    >
                      <Typography className={classes.sectionTitle} style={{ flex: 1, marginBottom: 0 }}>
                        Details
                      </Typography>
                      <IconButton size="small" className={classes.expandButton}>
                        {detailsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </div>
                    <Collapse in={detailsExpanded}>
                      <Typography variant="body2" style={{ fontSize: '0.85rem', marginTop: 8 }}>
                        {selectedSuggestion.description}
                      </Typography>
                    </Collapse>
                  </div>

                  {/* Location */}
                  <div className={classes.detailsSection}>
                    <Typography className={classes.sectionTitle}>Location</Typography>
                    <Chip 
                      label={selectedSuggestion.path}
                      size="small"
                      className={classes.pathChip}
                      variant="outlined"
                    />
                    <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        Line {selectedSuggestion.line}
                        {selectedSuggestion.endLine && selectedSuggestion.endLine !== selectedSuggestion.line && 
                          ` - ${selectedSuggestion.endLine}`}
                      </Typography>
                      {onGoToLine && (
                        <Button 
                          size="small" 
                          onClick={handleGoToSuggestion}
                          style={{ padding: '0 4px', minWidth: 'auto', fontSize: '0.7rem' }}
                        >
                          Go to
                        </Button>
                      )}
                    </Box>
                  </div>

                  {/* Diff Preview */}
                  {(selectedSuggestion.diff || selectedSuggestion.suggestedValue) && (
                    <div className={classes.detailsSection}>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={diffExpanded}
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setDiffExpanded(!diffExpanded)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDiffExpanded(!diffExpanded); }}
                      >
                        <Typography className={classes.sectionTitle} style={{ flex: 1, marginBottom: 0 }}>
                          Changes
                        </Typography>
                        <IconButton size="small" className={classes.expandButton}>
                          {diffExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </div>
                      <Collapse in={diffExpanded}>
                        <div className={classes.diffContainer}>
                          <div className={classes.diffHeader}>
                            <Typography variant="caption" color="textSecondary">
                              Diff Preview
                            </Typography>
                          </div>
                          <div className={classes.diffContent}>
                            {selectedSuggestion.diff?.removed && (
                              <span className={classes.removedLine}>
                                {selectedSuggestion.diff.removed}
                              </span>
                            )}
                            {(selectedSuggestion.diff?.added || selectedSuggestion.suggestedValue) && (
                              <span className={classes.addedLine}>
                                {selectedSuggestion.diff?.added || selectedSuggestion.suggestedValue}
                              </span>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className={classes.actionButtons}>
                    <Button
                      variant="contained"
                      size="small"
                      className={classes.acceptButton}
                      startIcon={<CheckIcon />}
                      onClick={() => onAcceptSuggestion(selectedSuggestion)}
                      fullWidth
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.rejectButton}
                      startIcon={<ClearIcon />}
                      onClick={() => onRejectSuggestion(selectedSuggestion)}
                      fullWidth
                    >
                      Dismiss
                    </Button>
                  </div>
                </Box>
              ) : (
                <Box mt={2} textAlign="center" color="text.secondary">
                  <Typography variant="caption">
                    Select a suggestion to see details
                  </Typography>
                </Box>
              )}
            </>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default AiSuggestionsSidebar;

